import { ErrorHandler, Injectable } from '@angular/core';

const reloadKey = 'cargomate:stale-build-reload-at';
const reloadCooldownMs = 60_000;
const staleBuildMarkers = [
  'failed to fetch dynamically imported module',
  'expected a javascript-or-wasm module script',
  'strict mime type checking is enforced for module scripts',
  'importing a module script failed',
  'error loading dynamically imported module',
  'chunkloaderror',
  'loading chunk'
];

@Injectable()
export class StaleBuildErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (recoverFromStaleBuildError(error)) {
      return;
    }

    console.error(error);
  }
}

export function installStaleBuildRecoveryListeners(): void {
  window.addEventListener('error', event => {
    if (recoverFromStaleBuildError(event.message) || recoverFromStaleBuildError(event.error)) {
      event.preventDefault();
    }
  }, true);

  window.addEventListener('unhandledrejection', event => {
    if (recoverFromStaleBuildError(event.reason)) {
      event.preventDefault();
    }
  });
}

export function recoverFromStaleBuildError(error: unknown): boolean {
  if (!isStaleBuildError(error)) {
    return false;
  }

  return scheduleStaleBuildReload();
}

function isStaleBuildError(error: unknown): boolean {
  const text = extractErrorText(error).toLowerCase();
  return staleBuildMarkers.some(marker => text.includes(marker));
}

function extractErrorText(error: unknown): string {
  if (!error) {
    return '';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    const cause = 'cause' in error ? extractErrorText(error.cause) : '';
    return [error.name, error.message, cause].filter(Boolean).join(' ');
  }

  if (typeof error === 'object') {
    const candidate = error as {
      message?: unknown;
      reason?: unknown;
      rejection?: unknown;
      error?: unknown;
      cause?: unknown;
    };

    return [
      extractErrorText(candidate.message),
      extractErrorText(candidate.reason),
      extractErrorText(candidate.rejection),
      extractErrorText(candidate.error),
      extractErrorText(candidate.cause)
    ].filter(Boolean).join(' ');
  }

  return String(error);
}

function scheduleStaleBuildReload(): boolean {
  const now = Date.now();
  const lastReloadAt = readLastReloadAt();

  if (lastReloadAt && now - lastReloadAt < reloadCooldownMs) {
    return false;
  }

  writeLastReloadAt(now);

  void clearRuntimeCaches().finally(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('app_reload', String(now));
    window.location.replace(url.toString());
  });

  return true;
}

function readLastReloadAt(): number {
  try {
    return Number(window.sessionStorage.getItem(reloadKey) || 0);
  } catch {
    return 0;
  }
}

function writeLastReloadAt(timestamp: number): void {
  try {
    window.sessionStorage.setItem(reloadKey, String(timestamp));
  } catch {
    return;
  }
}

async function clearRuntimeCaches(): Promise<void> {
  const tasks: Promise<unknown>[] = [];

  if ('caches' in window) {
    tasks.push(window.caches.keys().then(keys => Promise.all(keys.map(key => window.caches.delete(key)))));
  }

  if ('serviceWorker' in navigator) {
    tasks.push(navigator.serviceWorker.getRegistrations().then(registrations =>
      Promise.all(registrations.map(registration => registration.unregister()))
    ));
  }

  await Promise.allSettled(tasks);
}
